import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const BOMStockReport = () => {
  const { bomId: bomIdParam } = useParams();
  const location = useLocation();

  // -----------------------------
  // Helpers
  // -----------------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && val !== "" ? val : fallback;
  };

  const toInt = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const toStr = (v, fallback = "") => {
    if (v === null || v === undefined) return fallback;
    const s = String(v).trim();
    return s !== "" ? s : fallback;
  };

  const formatNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  };

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  // -----------------------------
  // localStorage values
  // -----------------------------
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // -----------------------------
  // parsed values
  // -----------------------------
  const branchid = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchtype = useMemo(() => toStr(branchTypeLS, "HO"), [branchTypeLS]);
  const userid = useMemo(() => toInt(userIdLS, 1), [userIdLS]);

  // -----------------------------
  // bomid from route OR query
  // -----------------------------
  const bomid = useMemo(() => {
    return (
      toInt(bomIdParam, 0) ||
      toInt(query.get("bomid"), 0) ||
      toInt(query.get("bomId"), 0) ||
      0
    );
  }, [bomIdParam, query]);

  // -----------------------------
  // common headers
  // -----------------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": toStr(finYearId, ""),
      "x-finyear": toStr(finYear, ""),
      "x-branchid": branchid,
      "x-branchtype": branchtype,
      "x-userid": userid,
    }),
    [finYearId, finYear, branchid, branchtype, userid]
  );

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // -----------------------------
  // total qty
  // -----------------------------
  const totalQty = useMemo(() => {
    if (!reportData?.items?.length) return 0;
    return reportData.items.reduce(
      (sum, it) =>
        sum +
        Number(
          it.reqQty ??
            it.currentStock ??
            it.stockQty ??
            it.fld_stockqty ??
            0
        ),
      0
    );
  }, [reportData]);

  const fetchReportData = async () => {
    if (!branchid) {
      setError("branchid missing in localStorage");
      return;
    }

    if (!bomid) {
      setError("bomid missing in URL. Use /bom-stock-report/:bomId OR ?bomid=12");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${BASE_URL}/api/bomlisting/printOne`, {
        params: { branchid, bomid },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        setError(res.data?.message || "Failed to load BOM stock report data");
        setReportData(null);
        return;
      }

      setReportData(res.data?.data || null);

      // Auto print
      setTimeout(() => window.print(), 400);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          "Server error while loading BOM stock report"
      );
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bomid, branchid]);

  return (
    <div className="max-w-full mx-auto p-6 bg-white font-sans text-xs print:p-0 print:max-w-none">
      <div className="border-4 border-black p-3">
        {/* Header Section */}
        <div className="border-2 border-black mb-0">
          <div className="grid grid-cols-[140px_1fr] border-b-2 border-black">
            <div className="border-r-2 border-black p-3 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="w-24 h-20 bg-black mx-auto flex items-center justify-center mb-1">
                  <div className="text-white text-2xl">📷</div>
                </div>
                <div className="text-[9px] font-bold mt-1">
                  NISSI INFOTECH PVT LTD
                </div>
              </div>
            </div>

            <div className="text-center py-2 px-4">
              <h1 className="text-base font-bold">Nissi Infotech</h1>
              <div className="text-[10px] mt-0.5">Nissi</div>
              <div className="text-xs font-bold mt-0.5">CIVIL CONSTRUCTION</div>
              <div className="text-[9px] mt-1">
                3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu,
                India
              </div>
              <div className="text-[9px]">
                Phone No: 9003744644 Email:
                infraconsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>

          <div className="text-center py-2 border-b-2 border-black font-bold text-sm">
            BOM Stock Report
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            <div className="mt-2">Loading report data...</div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            <div className="font-bold">Error loading report:</div>
            <div className="mt-1">{error}</div>
            <div className="mt-2 text-xs text-gray-700">
              Debug: branchid={branchid || "-"} | bomid={bomid || "-"} | userid={userid || "-"}
            </div>
            <button
              onClick={fetchReportData}
              className="mt-3 bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Report */}
        {!loading && reportData && (
          <>
            <div className="border-l-2 border-r-2 border-black mt-3">
              <div className="grid grid-cols-2 gap-x-8 p-3 text-xs">
                <div className="space-y-1.5">
                  <div className="flex">
                    <span className="font-semibold w-36">Date:</span>
                    <span>{reportData.entryDate || "-"}</span>
                  </div>
                  <div className="flex">
                    <span className="font-semibold w-36">Project Name:</span>
                    <span>{reportData.projectName || "-"}</span>
                  </div>
                  <div className="flex">
                    <span className="font-semibold w-36">Production Code:</span>
                    <span>{reportData.productionCode || "-"}</span>
                  </div>
                  <div className="flex">
                    <span className="font-semibold w-36">UOM:</span>
                    <span>{reportData.tarUOM || "-"}</span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex">
                    <span className="font-semibold w-36">Production Name:</span>
                    <span>{reportData.productionName || "-"}</span>
                  </div>
                  <div className="flex">
                    <span className="font-semibold w-36">Production Qty:</span>
                    <span>{formatNumber(reportData.productionQty || 0)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-l-2 border-r-2 border-b-2 border-black">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-black bg-gray-50">
                    <th className="border-r border-black px-2 py-2 text-center w-12">
                      S.No
                    </th>
                    <th className="border-r border-black px-2 py-2 text-left w-24">
                      Item Code
                    </th>
                    <th className="border-r border-black px-2 py-2 text-left">
                      Item Name
                    </th>
                    <th className="px-2 py-2 text-right w-32">Current Stock</th>
                  </tr>
                </thead>

                <tbody>
                  {!reportData.items || reportData.items.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="p-4 text-center text-gray-500">
                        No items available
                      </td>
                    </tr>
                  ) : (
                    <>
                      {reportData.items.map((item, index) => (
                        <tr key={index} className="border-b border-gray-300">
                          <td className="border-r border-black px-2 py-2 text-center">
                            {index + 1}
                          </td>
                          <td className="border-r border-black px-2 py-2">
                            {item.itemCode ?? item.fld_itemcode ?? "-"}
                          </td>
                          <td className="border-r border-black px-2 py-2">
                            {item.itemName ?? item.fld_itemname ?? "-"}
                          </td>
                          <td className="px-2 py-2 text-right">
                            {formatNumber(
                              item.reqQty ??
                                item.currentStock ??
                                item.stockQty ??
                                item.fld_stockqty ??
                                0
                            )}
                          </td>
                        </tr>
                      ))}

                      <tr className="border-t border-black font-semibold bg-gray-100">
                        <td
                          colSpan="3"
                          className="border-r border-black px-2 py-2 text-right"
                        >
                          Total :
                        </td>
                        <td className="px-2 py-2 text-right">
                          {formatNumber(totalQty)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-l-2 border-r-2 border-b-2 border-black">
              <div className="grid grid-cols-3 text-xs">
                <div className="border-r-2 border-black px-4 py-12 text-center">
                  <div className="font-semibold">Prepared By</div>
                </div>
                <div className="border-r-2 border-black px-4 py-12 text-center">
                  <div className="font-semibold">Checked By</div>
                </div>
                <div className="px-4 py-12 text-center">
                  <div className="font-semibold">Approved By</div>
                </div>
              </div>
            </div>

            <div className="mt-6 mb-2 text-[10px] text-gray-700 border-t-2 border-black pt-2 px-2">
              <div>
                Report generated by ERP-Software - www.infraconsoft.com owned by
                www.nissiinfotech.com
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          body { margin: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 0.5cm; }
        }
      `}</style>
    </div>
  );
};

export default BOMStockReport;